import { useQuery } from '@tanstack/react-query';

import { accountManagersQuery, type UserRef } from '../api/account-managers.ts';

// CLI-01 at v1.984: one picker for a client's, a quote's and a job's account manager. Someone who
// can no longer manage quotes stays shown on what they managed, so the record never reads as
// unassigned when it is not — but they are not offered for anything new.

export function AccountManagerPicker({
  label,
  hideLabel = false,
  current,
  value,
  disabled = false,
  onChange,
}: {
  label: string;
  hideLabel?: boolean;
  /** Who the record names now, as the API returned them; null for nobody. */
  current: UserRef | null;
  /** The selected id, when it differs from `current` (a form not yet saved). */
  value?: string | null;
  disabled?: boolean;
  onChange: (accountManagerId: string | null) => void;
}) {
  const people = useQuery(accountManagersQuery);
  const offered = people.data ?? [];
  const selected = value === undefined ? (current?.id ?? null) : value;
  const departed = current && !offered.some((p) => p.id === current.id) ? current : null;
  return (
    <label className="flex flex-col gap-1">
      <span className={hideLabel ? 'sr-only' : 'text-sm font-medium text-ink'}>{label}</span>
      <select
        className="input py-1"
        value={selected ?? ''}
        disabled={disabled || people.isPending}
        onChange={(e) => onChange(e.currentTarget.value || null)}
      >
        <option value="">None</option>
        {departed ? <option value={departed.id}>{departed.name} (inactive)</option> : null}
        {offered.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
      {people.isError ? (
        <span role="alert" className="text-sm text-accent-2-700">
          {people.error.message}
        </span>
      ) : null}
    </label>
  );
}

/** The account manager as a section of a quote's or job's page, editable at any stage. */
export function AccountManagerField({
  current,
  editable,
  saving,
  error,
  onChange,
}: {
  current: UserRef | null;
  editable: boolean;
  saving: boolean;
  error: Error | null;
  onChange: (accountManagerId: string | null) => void;
}) {
  return (
    <section className="flex flex-col gap-2" aria-labelledby="account-manager">
      <h2 id="account-manager" className="section-title">
        Account manager
      </h2>
      {editable ? (
        <div className="max-w-md">
          <AccountManagerPicker
            label="Account manager"
            hideLabel
            current={current}
            disabled={saving}
            onChange={onChange}
          />
        </div>
      ) : (
        <p>{current?.name ?? 'Unassigned'}</p>
      )}
      {error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {error.message}
        </p>
      ) : null}
    </section>
  );
}
